import React, { useState, useContext } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { LoadingView, NotificationContext } from "@solsys/solsys-reactjs";

const PART_ADDED_TYPE = "part_added";

const notificationEmailsQuery = gql`
  query notificationEmails($request: notification_email_list_request) {
    notification_email(notification_email_list_request: $request) {
      edges {
        id
        email
      }
    }
  }
`;

const bulkReplaceMutation = gql`
  mutation bulkReplaceNotificationEmails($request: notification_email_bulk_replace_request!) {
    notification_email_bulk_replace(request: $request) {
      ok
      edges {
        id
        email
      }
    }
  }
`;

export default function NotificationEmailsSettings() {
  const { addNotification } = useContext(NotificationContext);
  const [partAdded, setPartAdded] = useState(null);
  const [saving, setSaving] = useState(false);

  const { loading, refetch } = useQuery(notificationEmailsQuery, {
    variables: {
      request: {
        filters: [
          { field: "notification_type", operator: "=", value: PART_ADDED_TYPE },
        ],
        sorting: [{ field: "sequence", direction: "asc" }],
      },
    },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const rows = data?.notification_email?.edges ?? [];
      setPartAdded(rows.length ? rows.map((row) => row.email) : [""]);
    },
    onError: (err) => {
      addNotification("bad", err.message || "Failed to load notification emails");
      setPartAdded([""]);
    },
  });

  const [bulkReplace] = useMutation(bulkReplaceMutation);

  const updateEmail = (index, value) => {
    setPartAdded((rows) => rows.map((email, i) => (i === index ? value : email)));
  };

  const addRow = () => {
    setPartAdded((rows) => [...rows, ""]);
  };

  const removeRow = (index) => {
    setPartAdded((rows) => {
      if (rows.length <= 1) return [""];
      return rows.filter((_, i) => i !== index);
    });
  };

  const handleSave = async () => {
    const emails = partAdded.map((email) => email.trim()).filter(Boolean);

    setSaving(true);
    try {
      const result = await bulkReplace({
        variables: {
          request: {
            notification_type: PART_ADDED_TYPE,
            emails,
          },
        },
      });
      const saved = result.data?.notification_email_bulk_replace?.edges ?? [];
      setPartAdded(saved.length ? saved.map((row) => row.email) : [""]);
      addNotification("good", "Notification emails saved");
      refetch();
    } catch (err) {
      console.error(err);
      addNotification("bad", err.message || "Failed to save notification emails");
    } finally {
      setSaving(false);
    }
  };

  if (loading || partAdded === null) return <LoadingView />;

  return (
    <div className="notification-emails-settings" style={{ padding: "24px", maxWidth: "720px" }}>
      <h2 style={{ marginTop: 0 }}>Notification Emails</h2>
      <p style={{ color: "var(--text-muted, #666)", marginBottom: "32px" }}>
        Configure email addresses that receive automated notifications from E-Crane.
        Changes apply immediately after you save.
      </p>

      <section style={{ marginBottom: "32px" }}>
        <h3 style={{ marginBottom: "8px" }}>Parts added to tickets or services</h3>
        <p style={{ color: "var(--text-muted, #666)", fontSize: "14px", lineHeight: 1.5, marginBottom: "16px" }}>
          Recipients listed below are emailed when a technician adds a part (with a part number)
          to a ticket or service. The email includes the technician&apos;s name, part number,
          quantity, and whether it was added to a ticket or service. Recipients can reply
          directly to reach the technician who added the part.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {partAdded.map((email, index) => (
            <div
              key={`row-${index}`}
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
              }}
            >
              <input
                type="email"
                value={email}
                onChange={(e) => updateEmail(index, e.target.value)}
                placeholder="parts@example.com"
                style={{ flex: 1, padding: "8px" }}
              />
              <button type="button" onClick={() => removeRow(index)}>
                Remove
              </button>
            </div>
          ))}
        </div>

        <button type="button" onClick={addRow} style={{ marginTop: "12px" }}>
          + Add recipient
        </button>
      </section>

      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        style={{ padding: "10px 24px", fontWeight: "bold" }}
      >
        {saving ? "Saving…" : "Save"}
      </button>
    </div>
  );
}
